import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";

export const TOKEN_KEY = "cf.email.jwt";
export const USER_KEY = "cf.email.user";

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const setStoredToken = (t: string | null) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

// Listeners that the AuthProvider wires up to perform an in-app redirect
// rather than a hard window.location change.
type UnauthorizedHandler = () => void;
const unauthorizedHandlers = new Set<UnauthorizedHandler>();
export const onUnauthorized = (fn: UnauthorizedHandler) => {
  unauthorizedHandlers.add(fn);
  return () => unauthorizedHandlers.delete(fn);
};

export const client: AxiosInstance = axios.create({
  baseURL: "/email",
  timeout: 60_000,
});

client.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.set("Authorization", token.startsWith("Bearer ") ? token : `Bearer ${token}`);
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    const status = err.response?.status;
    if (status === 401 || status === 403) {
      setStoredToken(null);
      localStorage.removeItem(USER_KEY);
      unauthorizedHandlers.forEach((fn) => fn());
    }
    return Promise.reject(err);
  }
);

export function apiErrorMessage(err: unknown, fallback = "Request failed"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "string" && data.length < 500) return data;
    if (data && typeof data === "object") {
      const rec = data as Record<string, unknown>;
      if (typeof rec.message === "string") return rec.message;
      if (typeof rec.error === "string") return rec.error;
    }
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function raw<T = unknown>(cfg: AxiosRequestConfig): Promise<T> {
  const res = await client.request<T>(cfg);
  return res.data;
}
